import {Button, Flex, Group, Text, Textarea} from "@mantine/core";
import {useRef, useEffect, useState} from "react";
import {getHotkeyHandler, useElementSize, useMediaQuery} from "@mantine/hooks";
import {useAppSelector} from "../../../app/hooks";
import {Trans, useTranslation} from "react-i18next";
import {ChatInputTypePicker} from "./ChatInputTypePicker";
import {abortStream} from "../chatApi";
import IconArrowRight from "../../../assets/arrow-right.svg?react";
import IconArrowUp from "../../../assets/arrow-up.svg?react";
import IconPlayerStop from "../../../assets/player-stop.svg?react";
import classes from "./TopCommandInput.module.css";
import {
    selectActiveThread,
    selectIsActiveThreadStreaming,
} from "../threadsSlice";

interface TopCommandInputProps {
    variant: "splash" | "chat";
    value: string;
    onSendMessage(message: string): void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onChange(value: string): void;
    placeholder?: string;
    disabled?: boolean;
    showKaiBranding?: boolean;
    compact?: boolean;
}

export function TopCommandInput({
    variant,
    value,
    onSendMessage,
    onChange,
    onKeyDown,
    placeholder,
    disabled = false,
    showKaiBranding = false,
    compact = false,
}: TopCommandInputProps) {
    const {t} = useTranslation();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const isDesktop = useMediaQuery("(min-width: $mantine-breakpoint-sm)");
    const isStreaming = useAppSelector(selectIsActiveThreadStreaming);
    const activeThread = useAppSelector(selectActiveThread);

    // Auto-focus on desktop
    useEffect(() => {
        if (isDesktop) {
            inputRef.current?.focus();
        }
    }, [isDesktop]);

    const handleSubmit = () => {
        if (isStreaming || value.trim() === "") {
            return;
        }
        onSendMessage(value);
        if (isDesktop) {
            inputRef.current?.focus();
        }
    };

    const cancelStreaming = () => {
        if (activeThread?.streamId) {
            abortStream(activeThread.streamId);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Call custom onKeyDown first if provided
        onKeyDown?.(event);

        // If not prevented, handle enter key for submit
        if (!event.defaultPrevented) {
            const hotkeyHandler = getHotkeyHandler([["enter", handleSubmit]]);
            hotkeyHandler(event);
        }
    };

    // Dynamic wrapping calculation
    const {ref: wrapperRef, width: wrapperWidth} = useElementSize();
    const {ref: hiddenRef, width: hiddenWidth} = useElementSize();
    const {ref: buttonGroupRef, width: buttonGroupWidth} = useElementSize();
    const denominator = wrapperWidth - buttonGroupWidth;
    const shouldWrap = denominator > 0 && hiddenWidth / denominator > 0.9;

    // Determine variant-specific classes
    const outerGroupClass = `${classes.outerGroup} ${classes[`outerGroup--${variant}`]}`;
    const wrapClass = shouldWrap ? classes.outerGroupWrap : "";
    const inputClass = `${classes.input} ${classes[`input--${variant}`]}`;

    // Default placeholders based on variant,
    // This is a seperate component than CommandInput, in ExperimentalMode, refactor if we want to merge later.
    const finalPlaceholder = showKaiBranding
        ? ""
        : placeholder ||
          (variant === "splash"
              ? t("common.askQuestion")
              : t("input.placeholder.thread"));

    // Determine button behavior and icon
    const isButtonDisabled = isStreaming ? false : value.trim() === "";
    const buttonAction = isStreaming ? cancelStreaming : handleSubmit;
    const buttonTitle = isStreaming ? "Stop" : "Send";

    return (
        <Flex
            ref={wrapperRef}
            className={`${outerGroupClass} ${wrapClass}`}
            gap="xs"
            data-compact={compact ? "true" : undefined}
            onClick={() => inputRef.current?.focus()}
        >
            <div style={{flex: 1, position: "relative", minWidth: 0}}>
                <Textarea
                    ref={inputRef}
                    disabled={disabled}
                    className={classes.textareaWrapper}
                    classNames={{
                        input: inputClass,
                    }}
                    placeholder={finalPlaceholder}
                    aria-label={finalPlaceholder || t("common.askQuestion")}
                    value={value}
                    autosize
                    minRows={1}
                    autoFocus={isDesktop}
                    maxRows={13}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                />
                {showKaiBranding && !isFocused && !value && (
                    <div className={classes.kaiBrandingPlaceholder} aria-hidden>
                        <span>
                            <Trans
                                i18nKey="common.searchBox"
                                components={{
                                    strong: (
                                        <strong className={classes.kaiTag} />
                                    ),
                                }}
                            />
                        </span>
                    </div>
                )}
            </div>
            <Text ref={hiddenRef} className={classes.hiddenText}>
                {value}
            </Text>
            <Group
                ref={buttonGroupRef}
                className={classes.buttonGroup}
                gap="xs"
                wrap="nowrap"
                justify="flex-end"
                align="center"
            >
                <Button
                    className={classes.sendButton}
                    classNames={{
                        inner: classes.sendButtonInner,
                    }}
                    variant="subtle"
                    onClick={buttonAction}
                    disabled={isButtonDisabled}
                    title={buttonTitle}
                    aria-label={buttonTitle}
                >
                    <IconArrowRight
                        className={classes.iconChevronRight}
                    />
                </Button>
            </Group>
        </Flex>
    );
}
